import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

/**
 * Просмотр PDF постранично — порт `DocumentPages` Android-версии.
 *
 * **Документ целиком в память не грузится.** Том дела на триста листов открывается
 * так же быстро, как на две страницы: pdf.js читает файл по кускам, а картинка
 * рисуется только у той страницы, которая сейчас на экране. Бюджет — [NFR.md](docs/NFR.md).
 *
 * Воркер берётся из сборки, а не с CDN: приложение офлайн-первое, и внешних адресов
 * в нём нет вовсе — иначе документ не открылся бы там, где чаще всего нужен, в суде
 * без сети.
 *
 * Сама библиотека подгружается **по требованию**: она весит больше, чем всё остальное
 * приложение, а нужна только тому, кто открыл документ. Список дел не должен платить
 * за просмотрщик своим запуском ([NFR.md](docs/NFR.md)). Service worker всё равно
 * кладёт её в предзагрузку, поэтому офлайн от этого не страдает.
 */
type Pdfjs = Awaited<ReturnType<typeof loadPdfjs>>;

const loadPdfjs = () => import('pdfjs-dist');

let library: Pdfjs | null = null;

async function pdfjs() {
  if (!library) {
    library = await loadPdfjs();
    library.GlobalWorkerOptions.workerSrc = workerUrl;
  }
  return library;
}

export interface RenderedPage {
  /** Ширина и высота в точках при заданном масштабе. */
  width: number;
  height: number;
}

export interface PdfPages {
  pageCount: number;
  /** Рисует страницу (нумерация с единицы) в холст под заданную ширину. */
  render: (page: number, canvas: HTMLCanvasElement, width: number) => Promise<RenderedPage | null>;
  /** Размер страницы при ширине 1: нужен, чтобы занять место до отрисовки. */
  ratio: (page: number) => Promise<number>;
  close: () => Promise<void>;
}

export async function openPdf(content: Blob): Promise<PdfPages | null> {
  const bytes = new Uint8Array(await content.arrayBuffer());

  const { getDocument } = await pdfjs();
  const task = getDocument({ data: bytes });

  let document;
  try {
    document = await task.promise;
  } catch {
    // Битый или чужой файл — не повод показывать пустой экран без объяснения:
    // вызывающий покажет «не удалось открыть» и предложит «Поделиться».
    return null;
  }

  return {
    pageCount: document.numPages,

    async ratio(page) {
      const view = await document.getPage(page);
      const { width, height } = view.getViewport({ scale: 1 });
      return height / width;
    },

    async render(page, canvas, width) {
      const view = await document.getPage(page);
      const base = view.getViewport({ scale: 1 });
      const scale = width / base.width;
      const viewport = view.getViewport({ scale });

      const context = canvas.getContext('2d');
      if (!context) return null;

      // Плотность экрана учитывается здесь, а не в CSS: иначе на iPhone скан
      // выглядит размытым ровно так же, как снимок экрана вместо документа.
      const density = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(viewport.width * density);
      canvas.height = Math.floor(viewport.height * density);

      await view.render({ canvas, canvasContext: context, viewport, transform: [density, 0, 0, density, 0, 0] })
        .promise;

      return { width: viewport.width, height: viewport.height };
    },

    async close() {
      // Закрывается сама задача: с ней уходит и воркер, иначе он остаётся
      // висеть на каждый открытый документ.
      await task.destroy();
    },
  };
}

/**
 * Сколько страниц в PDF. `null` — файл не PDF или не читается.
 *
 * Считается при добавлении и при замене файла: без этого строка документа молчала бы
 * о толщине дела, а «9 стр.» — половина смысла этой строки.
 */
export async function pageCountOf(content: Blob): Promise<number | null> {
  const pdf = await openPdf(content);
  if (!pdf) return null;

  const count = pdf.pageCount;
  await pdf.close();
  return count;
}
